/*
  The home page (§4), rebuilt in Tailwind off MUI's Grid/Box/Typography and off the
  Emotion pragma.

  Modelled on card-grading-admin-ui's catalog page and its AlphabeticalGrid — a featured
  card grid over an A-Z list — minus everything HQ-only (§4.5): no drag-to-reorder, no
  custom-preset sort, no Edit / Add / Upload. pop-report is read-only.

  **The pipeline is §4.2 and the order of its stages is the feature:**

      featured:  category rule -> home search -> live only -> rank by cardsGraded desc
                                -> slice(10) -> if Sort = A-Z, re-order those ten
      A-Z:       category rule -> home search -> live only -> dedupe by id -> group
      Archive:   category rule -> home search -> archived, graded -> dedupe by id -> group

  The slice is NEVER taken alphabetically. Sorting by name before slicing gives the first
  ten games by name, which is exactly the admin behaviour D15 rejects; the Sort control
  only re-orders the ten most-graded games that already survived. None of that logic
  lives here — it is lib/home.ts, lib/featured.ts and lib/grouping.ts, all unit-tested,
  and this file composes them.

  Two deliberate divergences from admin, both recorded so a later reader does not "fix"
  them back:
    - **the home search filters BOTH sections.** Admin passes its search term only to the
      featured grid (AlphabeticalGrid.tsx:133-148).
    - **the search is accent-folded** (D17): `pokemon` matches `Pokémon`, the largest game
      on the site. A whitespace-only query is empty, not a query nothing matches.

  New Zealand Mint is excluded by `gameCode` as well as by category (§5.2, D16) — see the
  note in lib/home.ts for why the category rule alone is not enough.

  The 128 links here are the other half of the job (task 13's finding): the game name and
  its image both linked to /{gameCode} with no className at all, relying on the global
  `a { color: black }` that D25 deleted, so they rendered in the body colour and read as
  plain text in both themes. The text link now carries CARD_TITLE_LINK — page text at
  rest, gold and underlined on hover and focus; the image link stays out of the tab order
  and the accessibility tree, so a keyboard and a screen reader get one link per game
  rather than two.
*/

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import FilterSearch from 'components/FilterSearch';
import HomeShowcase from 'components/HomeShowcase';
import SearchCardsRow from 'components/SearchCardsRow';
import PopulationBreakdown from 'components/PopulationBreakdown';
import Select from 'components/Select';
import CategoryToggle from 'components/CategoryToggle';
import { useHomeCategory } from 'components/useHomeCategory';

import {
  DataTableEmpty,
  DataTableError,
  resolveTableStatus,
} from 'components/DataTable';
import {
  CARD_TITLE_LINK,
  TILE,
  TILE_COUNT,
  TILE_COUNT_FIGURE,
  TILE_COUNT_LABEL,
  TILE_LOGO_IMG,
  TILE_LOGO_PANEL,
  LAZY_IMG,
} from 'components/styles';
import {
  findDuplicateNames,
  formatCount,
  groupGamesAlphabetically,
  FEATURED_LIMIT,
  nameKey,
  partitionArchived,
  selectFeaturedGames,
  selectHomeGames,
} from 'lib';
import type { AlphabeticalGroup } from 'lib';

import { useGetGamesQuery } from 'services/pcgPublicV2';
import { GameSummaryDto } from 'types/v2/catalog';

/* Governs the featured section only (§4.1). "Most graded" is the default. */
type HomeSort = 'mostGraded' | 'alphabetical';

/*
  The TCG | Sports choice is not in the URL (§4.1, §10 forbids new URL surface). Since
  batch 3 it is remembered in localStorage and shared with the header's Games menu
  (useHomeCategory, docs/specs/pop-report-batch3.md D2, D3), so it survives Back out of a
  game. The sort still resets on every mount.
*/
const SORT_OPTIONS = [
  { value: 'mostGraded', label: 'Most graded' },
  { value: 'alphabetical', label: 'A–Z' },
];

/* One string for both sections (§4.3); a zero-match query empties both. */
const EMPTY_MESSAGE = 'No games match that search.';

const FEATURED_GRID =
  'grid list-none grid-cols-2 gap-4 p-0 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5';

/*
  Columns, not a grid — the one place this deliberately does not copy admin.

  Admin's AlphabeticalGrid is `grid ... sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4`
  (sections/catalog/AlphabeticalGrid.tsx:222) and this was too, which means every cell in
  a row is as tall as the tallest cell in that row. Admin gets away with it; this data
  does not. Measured on the live catalogue at 1280px: "D" holds seven games and makes its
  row 246px, while A, B and C hold one game each — around 190px of white space under each
  of them, three times over. That was a good part of what read as loose next to admin.

  CSS columns size to their content, so a letter with one game takes one line. Groups get
  `break-inside-avoid` so a letter's heading never parts company with its games. The flow
  turns vertical — A B C down the first column rather than across the top — which is how
  an alphabetical index is normally read anyway.

  Home's content runs wider than the other pages (Layout), so from 2xl it takes a fifth
  column.
*/
const ALPHABETICAL_GRID =
  'list-none columns-1 gap-x-10 p-0 sm:columns-2 lg:columns-3 xl:columns-4 2xl:columns-5';

const SECTION_HEADING = 'font-display text-xl font-semibold text-pcg-text sm:text-2xl';

/* The loading grid mirrors the real one, so the page does not reflow when it arrives. */
const SKELETON_TILES = FEATURED_LIMIT;

function HomeView() {
  const { data: games = [], isLoading, error } = useGetGamesQuery();

  /* Shared with the header's Games menu and remembered (docs/specs/pop-report-batch3.md D2). */
  const [category] = useHomeCategory();
  const [sort, setSort] = useState<HomeSort>('mostGraded');
  const [query, setQuery] = useState('');

  /* Stages one and two, shared by both sections — the only place they are applied. */
  const matching = useMemo(
    () => selectHomeGames(games, category, query),
    [games, category, query]
  );

  /* Stage three: archived games leave Featured and "All …" for the Archive section. */
  const { live, archived } = useMemo(() => partitionArchived(matching), [matching]);

  /* Over every game on the page, so a pair split across two sections is still labelled. */
  const duplicateNames = useMemo(() => findDuplicateNames(matching), [matching]);

  const featured = useMemo(
    () => selectFeaturedGames(live, { alphabetical: sort === 'alphabetical' }),
    [live, sort]
  );

  const groups = useMemo(() => groupGamesAlphabetically(live), [live]);
  const archiveGroups = useMemo(() => groupGamesAlphabetically(archived), [archived]);

  const countGames = (sectionGroups: AlphabeticalGroup<GameSummaryDto>[]) =>
    sectionGroups.reduce((count, group) => count + group.games.length, 0);
  const alphabeticalCount = countGames(groups);
  const archiveCount = countGames(archiveGroups);

  /* One status for both live sections: they are two views of the same request and the same pool. */
  const status = resolveTableStatus(isLoading, error, live.length);

  /* Hidden while loading, on error and when empty; it has no state of its own to show. */
  const showArchive = !isLoading && !error && archiveCount > 0;

  /*
    The count is interpolated rather than written into the string twice. These headings
    promise a number, and a hardcoded "Top 10" beside a FEATURED_LIMIT of 8 is a lie the
    compiler cannot catch — which is exactly what happened when the limit moved.
  */
  const featuredHeading =
    category === 'Sports'
      ? `Top ${FEATURED_LIMIT} Graded Sports Cards`
      : `Top ${FEATURED_LIMIT} Most Graded TCGs`;
  const alphabeticalHeading = category === 'Sports' ? 'All Sports Cards' : 'All TCGs';

  /* The name, plus a muted game code when another game on the page has the same name. */
  const renderGameName = (game: GameSummaryDto) => (
    <>
      {game.name}
      {duplicateNames.has(nameKey(game.name)) ? (
        <span className="ml-1.5 text-xs font-normal text-pcg-textMuted">{game.gameCode}</span>
      ) : null}
    </>
  );

  /*
    Admin's catalogue tile (see the TILE constants in components/styles.ts): the logo on
    white, then the name, then the count. Games get no chips — a game carries every
    language its sets do, and Pokémon alone would wear three of them.

    The logo used to sit straight on the #12161F card. Half this catalogue's logos are
    black line art drawn for paper — One Piece is black type on a transparent ground — so
    they were being painted invisible. That is the substance of this change; the rest is
    that a centred stack of name, count and language reads as loose where a row does not.
  */
  const renderFeaturedCard = (game: GameSummaryDto) => {
    const to = `/${game.gameCode}`;
    return (
      <li key={game.id} className={TILE}>
        {/*
          The same destination as the name below it. Out of the tab order and out of the
          accessibility tree so a keyboard and a screen reader get one link per game;
          tabIndex -1 first, since aria-hidden on something focusable is its own violation.
        */}
        <Link to={to} tabIndex={-1} aria-hidden="true" className={TILE_LOGO_PANEL}>
          {game.imageUrl ? (
            <img src={game.imageUrl} alt="" {...LAZY_IMG} className={TILE_LOGO_IMG} />
          ) : (
            <span className={`${TILE_LOGO_IMG} block`} />
          )}
        </Link>
        <div className="flex flex-1 flex-col gap-2 px-3 py-2.5">
          <h3 className="skin-tile-title font-display text-sm leading-tight">
            <Link to={to} title={game.name} className={`line-clamp-2 ${CARD_TITLE_LINK}`}>
              {renderGameName(game)}
            </Link>
          </h3>
          <p className={TILE_COUNT}>
            <span className={TILE_COUNT_FIGURE}>{formatCount(game.cardsGraded)}</span>{" "}
            <span className={TILE_COUNT_LABEL}>graded</span>
          </p>
        </div>
      </li>
    );
  };

  /* The A-Z list, shared by "All …" and the Archive section. */
  const renderAlphabeticalGroups = (sectionGroups: AlphabeticalGroup<GameSummaryDto>[]) => (
    <ul className={ALPHABETICAL_GRID}>
      {sectionGroups.map(group => (
        <li key={group.key} className="mb-6 break-inside-avoid">
          <div className="mb-1 flex items-center gap-3">
            <h3 className="font-display text-lg font-bold leading-none text-pcg-goldText">
              {group.key}
            </h3>
            <span aria-hidden="true" className="h-px flex-1 bg-pcg-border" />
          </div>
          <ul className="list-none p-0">
            {group.games.map(game => (
              <li
                key={game.id}
                className="flex items-center justify-between gap-3 py-1"
              >
                {/*
                  Light text, not link gold — the same rule as the tiles above.
                  A column of 50 gold underlined names was most of why this page
                  read busier than admin's.
                */}
                <Link
                  to={`/${game.gameCode}`}
                  title={game.name}
                  className={`truncate font-medium ${CARD_TITLE_LINK}`}
                >
                  {renderGameName(game)}
                </Link>
                <span className="shrink-0 text-sm font-semibold tabular-nums text-pcg-goldText">
                  {formatCount(game.cardsGraded)}
                </span>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="skin-stack flex flex-col gap-8">
      {/*
        No breadcrumb trail on home (Robert, 2026-10-20): a lone "Home" crumb on the home
        page pointed nowhere. The other views keep theirs.

        The top, laid out like the events storefront: banner image beside the Population
        Report card.
      */}
      <HomeShowcase />

      {/*
        The cert lookup beside the population search (Robert, 2026-10-20), split by a gold
        rule that fades out at both ends. Below `lg` they stack and the rule goes.
      */}
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_1px_minmax(0,1fr)] lg:gap-x-10">
        <SearchCardsRow belowBanner />
        <span
          aria-hidden="true"
          className="hidden w-px self-stretch bg-gradient-to-b from-transparent via-pcg-gold to-transparent lg:block"
        />
        {/* §12.31: the page's one h1, above the controls it introduces. */}
        <div className="flex flex-col gap-4">
          {/*
            The cert lookup's own eyebrow, heading and rule classes, so the two headings sit
            on one line across the row (the eyebrow and the rule are the event skin's; other
            skins hide both on each side alike).
          */}
          <div>
            <p className="skin-eyebrow hidden">Browse by game</p>
            <h1 className="skin-hero-heading mb-4 text-3xl text-pcg-text [text-wrap:balance]">
              Population Report
            </h1>
            <span aria-hidden="true" className="skin-hero-rule hidden" />
          </div>

          {/* Half the row now, so the search takes a line and the two selects share the next. */}
          <div className="grid gap-3 sm:grid-cols-2">
            <FilterSearch
              onChange={setQuery}
              label="Search games"
              placeholder="Search games"
              className="w-full sm:col-span-2"
            />
            <CategoryToggle variant="page" className="w-full" />
            <Select
              label="Sort"
              value={sort}
              options={SORT_OPTIONS}
              onChange={value => setSort(value as HomeSort)}
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/*
        The site-wide breakdown (§9.2). Global scope needs no id, so it never hides for
        want of one — only because the endpoint is not there yet (D11).
      */}
      <PopulationBreakdown scope="global" />

      {/*
        Featured (§4.3). The heading does not change with the sort (D15): under A-Z these
        are still the ten most-graded games, displayed alphabetically.
      */}
      <section aria-labelledby="home-featured-heading" className="flex flex-col gap-4">
        {/* Heading, hairline, count on the right — admin's SectionHeading. */}
        <div className="flex items-center gap-3">
          <h2 id="home-featured-heading" className={SECTION_HEADING}>
            {featuredHeading}
          </h2>
          <span aria-hidden="true" className="skin-section-rule h-px flex-1 bg-pcg-goldMuted" />
          <span className="shrink-0 text-xs uppercase tracking-wide text-pcg-textMuted">
            Ranked by cards graded
          </span>
        </div>

        {status === 'loading' ? (
          /* Tiles the size of the real cards, so nothing shoves the page when they land. */
          <ul aria-hidden="true" className={FEATURED_GRID}>
            {Array.from({ length: SKELETON_TILES }).map((_unused, index) => (
              <li key={`featured-skeleton-${index}`} className={TILE}>
                <span className="block h-[122px] w-full animate-pulse bg-pcg-raised" />
                <span className="m-3 block h-4 w-3/4 animate-pulse rounded bg-pcg-raised" />
                <span className="mx-3 mb-3 block h-3 w-1/3 animate-pulse rounded bg-pcg-raised" />
              </li>
            ))}
          </ul>
        ) : null}

        {status === 'error' ? <DataTableError /> : null}
        {status === 'empty' ? <DataTableEmpty title={EMPTY_MESSAGE} /> : null}

        {status === 'rows' ? (
          <ul className={FEATURED_GRID}>{featured.map(renderFeaturedCard)}</ul>
        ) : null}
      </section>

      {/* A-Z (§4.4). Every matching game in the category, grouped, with "#" last. */}
      <section aria-labelledby="home-alphabetical-heading" className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <h2 id="home-alphabetical-heading" className={SECTION_HEADING}>
            {alphabeticalHeading}
          </h2>
          <span aria-hidden="true" className="skin-section-rule h-px flex-1 bg-pcg-goldMuted" />
          {status === 'rows' ? (
            <span className="shrink-0 text-xs uppercase tracking-wide text-pcg-textMuted">
              {formatCount(alphabeticalCount)}{' '}
              {alphabeticalCount === 1 ? 'game' : 'games'}
            </span>
          ) : null}
        </div>

        {status === 'loading' ? (
          <ul aria-hidden="true" className={ALPHABETICAL_GRID}>
            {Array.from({ length: 4 }).map((_unused, index) => (
              <li
                key={`alphabetical-skeleton-${index}`}
                className="mb-6 flex break-inside-avoid flex-col gap-2"
              >
                <span className="h-5 w-8 animate-pulse rounded bg-pcg-raised" />
                <span className="h-4 w-full animate-pulse rounded bg-pcg-raised" />
                <span className="h-4 w-2/3 animate-pulse rounded bg-pcg-raised" />
              </li>
            ))}
          </ul>
        ) : null}

        {status === 'error' ? <DataTableError /> : null}
        {status === 'empty' ? <DataTableEmpty title={EMPTY_MESSAGE} /> : null}

        {status === 'rows' ? renderAlphabeticalGroups(groups) : null}
      </section>

      {/*
        Archive. Archived games that still have gradings, after "All …" and under the same
        category and search. Never in the Top 10.
      */}
      {showArchive ? (
        <section aria-labelledby="home-archive-heading" className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <h2 id="home-archive-heading" className={SECTION_HEADING}>
              Archive
            </h2>
            <span aria-hidden="true" className="skin-section-rule h-px flex-1 bg-pcg-goldMuted" />
            <span className="shrink-0 text-xs uppercase tracking-wide text-pcg-textMuted">
              {formatCount(archiveCount)} {archiveCount === 1 ? 'game' : 'games'}
            </span>
          </div>
          {renderAlphabeticalGroups(archiveGroups)}
        </section>
      ) : null}
    </div>
  );
}

export default HomeView;
